import { lazy } from "react";
import { BrowserRouter, Link, Routes, Route, Navigate } from "react-router-dom";
import { LinkProvider } from "@jkast90/kast-common/ui";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import { SessionProvider, useSession } from "@jkast90/kast-common/auth";
import { useCacheScopedToUser } from "@jkast90/kast-common/query-client";
import { queryClient } from "@common/queryClient";
import { store } from "@common/store/store";
import { session } from "@common/auth/session";
import HomePage from "./pages/HomePage";
import ProtectedRoute from "./auth/components/ProtectedRoute";
import FloatingCartButton from "@common/components/FloatingCartButton";
import SiteLayout from "@common/components/SiteLayout";

// The landing page ships in the entry chunk; every other route loads on demand.
const AboutPage = lazy(() => import("./pages/AboutPage"));
const RoadAdoptionsPage = lazy(() => import("./pages/RoadAdoptionsPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const WorkWithUsPage = lazy(() => import("./pages/WorkWithUsPage"));
const ImpactPage = lazy(() => import("./pages/ImpactPage"));
const SubmitPage = lazy(() => import("./pages/SubmitPage"));
const BugReportPage = lazy(() => import("./pages/BugReportPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const AdminPanel = lazy(() => import("./admin/pages/AdminPanel"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const OrderConfirmationPage = lazy(() => import("./pages/OrderConfirmationPage"));

/** Drops the cache whenever someone else signs in (or out). */
function CacheScope() {
  useCacheScopedToUser(queryClient, useSession().user?.sub || null);
  return null;
}

export default function App() {
  return (
    <SessionProvider session={session}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <CacheScope />
          <BrowserRouter>
            <LinkProvider link={Link}>
              <Routes>
                <Route element={<SiteLayout />}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/road-adoptions" element={<RoadAdoptionsPage />} />
                  <Route path="/work-with-us" element={<WorkWithUsPage />} />
                  <Route path="/partners-sponsors" element={<Navigate to="/work-with-us" replace />} />
                  <Route path="/gallery" element={<GalleryPage />} />
                  <Route path="/donations" element={<Navigate to="/work-with-us" replace />} />
                  <Route path="/contact" element={<Navigate to="/work-with-us" replace />} />
                  <Route path="/impact" element={<ImpactPage />} />
                  <Route path="/submit" element={<SubmitPage />} />
                  <Route path="/bug-report" element={<BugReportPage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/shop/items/:itemId" element={<ProductDetailPage />} />
                  <Route path="/shop/cart" element={<CartPage />} />
                  <Route path="/shop/checkout" element={<CheckoutPage />} />
                  <Route path="/shop/orders/:orderId" element={<OrderConfirmationPage />} />
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/admin" element={<Navigate to="/admin/summary" replace />} />
                  <Route
                    path="/admin/:tab"
                    element={
                      <ProtectedRoute>
                        <AdminPanel />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
              <FloatingCartButton />
            </LinkProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </Provider>
    </SessionProvider>
  );
}
